import {
  type AttentionItem,
  sortAttention,
} from '../../components/common/detailChrome';
import { formatRelativeTime } from '../../lib/formatters';
import type { SuperPublisherDetailData, SuperPublisherTabKey } from './types';

/** Pure derivations for the publisher detail page, kept out of React. */

export type PublisherAttentionItem = AttentionItem<SuperPublisherTabKey>;

type Publisher = SuperPublisherDetailData['publisher'];

export function publisherDisplayName(publisher: Publisher): string {
  return (
    publisher.organization?.name ||
    publisher.individual?.name ||
    publisher.user.fullName ||
    publisher.user.email ||
    'Publisher'
  );
}

export function publisherWebsite(publisher: Publisher): string | null {
  return (
    publisher.organization?.website || publisher.individual?.website || null
  );
}

export function sumsubApplicantUrl(applicantId: string): string {
  return `https://cockpit.sumsub.com/checkus/applicant/${applicantId}/basicInfo?clientId=solana`;
}

export function explorerUrl(path: string, cluster: string | null): string {
  return `https://explorer.solana.com/${path}?cluster=${cluster ?? 'mainnet'}`;
}

/** What an admin should look at first, most severe first. Empty means all clear. */
export function deriveAttention(
  data: SuperPublisherDetailData,
  context: { isAdmin: boolean; now?: number },
): PublisherAttentionItem[] {
  const { publisher, stats } = data;
  const kyb = publisher.kybSubmission;
  const items: PublisherAttentionItem[] = [];
  const kybAction = context.isAdmin
    ? { label: 'KYB', tab: 'kyb' as const }
    : undefined;
  const dappsAction = { label: 'Dapps', tab: 'dapps' as const };

  if (publisher.isFrozen) {
    items.push({
      id: 'frozen',
      tone: 'error',
      title: 'Publisher is frozen',
      detail: 'Members cannot add dapps or upload new versions.',
    });
  }

  if (kyb) {
    switch (kyb.status) {
      case 'Rejected':
        items.push({
          id: 'kyb-rejected',
          tone: 'error',
          title: 'Verification was rejected',
          detail: kyb.rejectionReason,
          action: kybAction,
        });
        break;
      case 'RequiresAdditionalInfo':
        items.push({
          id: 'kyb-info',
          tone: 'warning',
          title: 'Verification needs more information',
          detail: kyb.additionalInfoRequested,
          action: kybAction,
        });
        break;
      case 'UnderReview':
        items.push({
          id: 'kyb-review',
          tone: 'info',
          title: 'Verification is under review',
          detail: kyb.submittedAt
            ? `Submitted ${formatRelativeTime(kyb.submittedAt)}`
            : null,
          action: kybAction,
        });
        break;
      case 'InProgress':
        items.push({
          id: 'kyb-progress',
          tone: 'info',
          title: 'Verification is in progress',
          action: kybAction,
        });
        break;
      case 'NotStarted':
        if (!publisher.kycKybBypassEnabled) {
          items.push({
            id: 'kyb-not-started',
            tone: 'warning',
            title: 'Verification not started',
            action: kybAction,
          });
        }
        break;
      default:
        break;
    }
  } else if (!publisher.kycKybBypassEnabled) {
    items.push({
      id: 'kyb-missing',
      tone: 'warning',
      title: 'No verification on file',
      action: kybAction,
    });
  }

  if (publisher.kycKybBypassEnabled) {
    items.push({
      id: 'bypass',
      tone: 'info',
      title: 'KYC/B bypass is enabled',
      detail: 'Verification is not required for this publisher.',
    });
  }

  if (context.isAdmin && !publisher.user.emailVerified) {
    items.push({
      id: 'email-unverified',
      tone: 'warning',
      title: 'Account email is not verified',
      detail: publisher.user.email,
    });
  }

  if (context.isAdmin && publisher.user.onboardingState !== 'Complete') {
    items.push({
      id: 'onboarding',
      tone: 'info',
      title: `Onboarding stopped at ${publisher.user.onboardingState}`,
    });
  }

  if (stats.rejectedDapps > 0) {
    items.push({
      id: 'dapps-rejected',
      tone: 'warning',
      title: `${stats.rejectedDapps} rejected ${stats.rejectedDapps === 1 ? 'dapp' : 'dapps'}`,
      action: dappsAction,
    });
  }

  if (stats.underReviewDapps > 0) {
    items.push({
      id: 'dapps-review',
      tone: 'info',
      title: `${stats.underReviewDapps} ${stats.underReviewDapps === 1 ? 'dapp' : 'dapps'} under review`,
      action: dappsAction,
    });
  }

  return sortAttention(items);
}
